import React, { useState, useEffect, useRef } from "react"
import { Box } from "rebass"
import styled from "@emotion/styled"
import { useIntersectionObserver } from "react-intersection-observer-hook"
import { motion } from "framer-motion"

const LOOP_STARTS_AT = 0
const LOOP_EVERY = 6.5

const PlayIcon = () => {
  return (
    <svg width="17px" height="22px" viewBox="0 0 17 22" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <polygon id="Triangle" fill="#FFFFFF" transform="translate(8.500000, 11.000000) rotate(-270.000000) translate(-8.500000, -11.000000) " points="8.5 2.5 19.5 19.5 -2.5 19.5"></polygon>
      </g>
    </svg>
  );
}

const StopIcon = () => {
  return (
    <svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect id="Rectangle" fill="#FFFFFF" x="0" y="0" width="20" height="20"></rect>
      </g>
    </svg>
  )
}

const VideoPlayer = ({ video, videoMobile }) => {
  const [muted, setMuted] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)
  const [progress, setProgress] = useState(0)
  const videoRef = useRef()
  const [ref, { entry }] = useIntersectionObserver()
  const isVisible = entry && entry.isIntersecting

  useEffect(() => {
    if (!isVisible && fullscreen) toggleFullscreen()
  }, [isVisible])

  const toggleFullscreen = () => {
    setFullscreen(fullscreen ? false : true)
    setMuted(muted ? false : true)
    videoRef.current.currentTime = !fullscreen ? 0 : LOOP_STARTS_AT
    // will snap video to viewport after has started
    if (!fullscreen) {
      setTimeout(() => {
        videoRef.current.scrollIntoView({ behavior: "smooth" })
      }, 1000);
    }
  }

  // method handling video loop
  const playbackObserver = (firstRender = false, restart = false) => {
    let { currentTime, duration } = videoRef.current

    // set current video progress
    setProgress((currentTime * 100) / duration)

    // jump video ahead on initial render, or force restart
    if ((!fullscreen && firstRender) || restart) {
      videoRef.current.currentTime = LOOP_STARTS_AT
      videoRef.current.play()
    }

    // loop video when not in fullscreen
    if (
      !fullscreen &&
      (currentTime >= LOOP_EVERY || currentTime === duration)
    ) {
      videoRef.current.currentTime = LOOP_STARTS_AT
      videoRef.current.play()
    }
  }

  return (
    <Box sx={{ position: "relative" }} ref={ref}>
      <Box
        as={PlayButton}
        sx={{
          size: ["60px", "70px"],
          bottom: [0, null, `50px`],
          left: ['20px', null, `50px`],
        }}
        aria-label={!fullscreen ? "Jouer" : "Stop"}
        onClick={() => toggleFullscreen()}
      >
        <motion.div
          className="inner"
          whileHover={{ scale: 1.35 }}
          whileTap={{ scale: 0.9 }}
          transition={{ duration: 0.125 }}
        >
          <span className="label">{fullscreen ? <StopIcon/> : <PlayIcon/>}</span>
          {fullscreen && <progress value={progress * 0.01} />}
        </motion.div>
      </Box>
      <Box
        as={VideoContainer}
        sx={{
          height: fullscreen ? `100vh` : [`30vh`, null, null, `60vh`],
        }}
      >
        <Box
          as={`video`}
          ref={videoRef}
          muted={muted}
          autoPlay
          loop={!fullscreen}
          playsInline
          disableremoteplayback="true"
          onLoadStart={e => playbackObserver(true)}
          onTimeUpdate={e => playbackObserver()}
          onEnded={e => {
            toggleFullscreen()
            playbackObserver(null, true)
          }}
          sx={{
            objectFit: fullscreen ? "contain" : "cover",
            width: `100%`,
            height: `100%`,
          }}
        >
          {videoMobile && (
            <source src={video.mediaItemUrl} media="(min-width: 992px)" />
          )}
          {videoMobile && <source src={videoMobile.mediaItemUrl} />}
          {!videoMobile && <source src={video.mediaItemUrl} />}
        </Box>
      </Box>
    </Box>
  )
}

export default VideoPlayer

const PlayButton = styled.button`
  position: absolute;
  z-index: 10;
  border: none;
  cursor: pointer;
  padding: 0;
  background: transparent;

  .inner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.radii.circle};
    background-color: ${props => props.theme.colors.primary};
    font-family: ${props => props.theme.fonts.monospace};
    overflow: hidden;
  }

  .label {
    position: relative;
    z-index: 2;
    mix-blend-mode: difference;
    line-height: 1;
    display: block;
    height: auto;
  }

  svg {
    display: block;
  }

  progress {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    appearance: none;
    &::-webkit-progress-bar {
      background-color: transparent;
    }
    &::-webkit-progress-value {
      background-color: ${props => props.theme.colors.secondary};
    }
  }

  &:hover {
    .inner {
      background-color: ${props => props.theme.colors.secondary};
    }
  }

  &:focus {
    outline: none;
  }
`

const VideoContainer = styled.div`
  width: 100%;
  transition: height 0.65s ease-in-out 0.5s;
`
